import React from 'react';
import PropTypes from 'prop-types';

import SiteHeader from '../components/SiteHeader';
import URLSearchParams from 'url-search-params';

import '../string-polyfill';
import {isAffiliate} from '../utils/affiliate.utils';
import ConnectorContext from '../components/context/ConnectorContext';

export class SiteHeaderConnector extends React.Component {
    static propTypes = {
        bceHeaderUrl: PropTypes.string,
        currencyCode: PropTypes.string,
        dataUrl: PropTypes.string,
        displayNotificationBar: PropTypes.bool,
        displaySignUpLink: PropTypes.bool,
        displayTrayJewel: PropTypes.bool,
        bgWhite: PropTypes.bool,
        fetchUserData: PropTypes.bool,
        fullLocale: PropTypes.string,
        isInverse: PropTypes.bool,
        logoutRedirectUrl: PropTypes.string,
        mcsData: PropTypes.shape({}),
        /**
         * Overrides default implementation of Global selector change handler. See docs for example of usage.
         */
        onGlobalSelectorChange: PropTypes.func,
        onNotificationBarDismiss: PropTypes.func,
        onNotificationMessageDismiss: PropTypes.func,
        notificationMessages: PropTypes.arrayOf(PropTypes.string),
        siteName: PropTypes.string,
        theme: PropTypes.string,
        tripboard: PropTypes.oneOfType([PropTypes.node, PropTypes.element, PropTypes.func]),
        /**
         * Overrides default generated locale switcher link.
         * e.g. if current site is Vrbo and location is https://www.vrbo.com/vacation-rental/p123
         * The corresponding property on homeaway_ca site is https://www.vrbo.com/en-ca/cottage-rental/p123
         * Put the following to override default locale switcher redirection link
         * [{ locale: 'en_CA', link: 'https://www.vrbo.com/en-ca/cottage-rental/p123' }]
         */
        localeSwitcherOverrides: PropTypes.arrayOf(PropTypes.shape({
            locale: PropTypes.string,
            link: PropTypes.string,
        })),
        withoutCountryLanguageSelector: PropTypes.bool,
        withoutCurrencySelector: PropTypes.bool,
        withoutFlags: PropTypes.bool,
        withoutHelpLinks: PropTypes.bool,
        withoutInbox: PropTypes.bool,
        withoutListYourProperty: PropTypes.bool,
        withoutLogin: PropTypes.bool,
        withoutScratchpad: PropTypes.bool,
        withoutSearch: PropTypes.bool,
        withoutSignUp: PropTypes.bool,
        withoutTraveler: PropTypes.bool,
        withoutAccessibility: PropTypes.bool,
        withoutLocaleSwitcher: PropTypes.bool
    };

    static defaultProps = {
        bceHeaderUrl: 'bceHeader',
        currencyCode: 'USD',
        dataUrl: '/api/header',
        displayNotificationBar: false,
        displayTrayJewel: false,
        fetchUserData: true,
        fullLocale: 'en_US_HA',
        siteName: 'homeaway_us'
    };

    constructor(props) {
        super(props);

        this.state = {
            ...props.data,
        };
        this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    }

    componentDidMount() {
        if (this.props.fetchUserData) {
            this.getUserData();
        }

        document.addEventListener('visibilitychange', this.handleVisibilityChange);
    }

    componentWillReceiveProps(props) {
        if (this.props.fetchUserData !== props.fetchUserData
            && props.fetchUserData) {
            this.getUserData(props);
        }
    }

    handleVisibilityChange() {
        if (!document.hidden) {
            if (this.props.fetchUserData) {
                this.getUserData();
            }
        }
    }

    formatDataUrl(dataUrl) {
        dataUrl = dataUrl.startsWith('/') ? dataUrl : `/${dataUrl}`;
        dataUrl = dataUrl.endsWith('/') ? dataUrl : `${dataUrl}/`;

        return dataUrl;
    }

    getDiscoveryFeatureByVariant() {
        const discoveryFeatures = ['show-discover', 'show-explore', 'show-inspire'];
        const {clientDiscovery: {discoveryVariant} = {}} = this.props;
        return discoveryFeatures[Number(discoveryVariant) - 1] || discoveryFeatures[0];
    }

    /* eslint-disable complexity */
    getUserData(props = this.props) {
        let dataUrl = this.formatDataUrl(props.dataUrl);
        let url = `${dataUrl}${props.bceHeaderUrl}`;
        let params = new URLSearchParams();

        params.append('site', props.siteName);

        /* istanbul ignore else */
        if (props.fullLocale) {
            params.append('locale', props.fullLocale);
        }

        /* istanbul ignore else */
        if (props.isInverse) {
            params.append('feature', 'theme-inverse');
        }


        /* istanbul ignore else */

        params.append('feature', 'currency-selector');

        params.append('feature', this.getDiscoveryFeatureByVariant());


        // If there is no mcsData, append the current URL as a 'visitorUrl'. Then when we proxy through /bceHeader, MCS will
        // still have the opportunity to see the original URL, which may include utm_campaign etc. If we don't append
        // the current URL here (as visitorUrl), then MCS will only see <domain>/api/bceHeader?site=homeaway_us&locale=en_US
        if (!props.mcsData) {
            let visitorUrl = window.location && window.location.href;
            /* istanbul ignore else */
            if (visitorUrl) {
                params.append('visitorUrl', visitorUrl);
            }
        } else if (props.mcsData && isAffiliate(props.mcsData) && props.mcsData.attributes) {
            // If traveler apps have passed in mcsData as a prop, append an 'affiliateCampaign'. Then we can skip the call
            // to MCS and grab the affiliate via the affiliateCampaign.
            params.append('affiliateCampaign', `${props.mcsData.attributes.affiliate}_${props.mcsData.attributes.campaignId}`);
        }

        // eslint-disable-next-line complexity
        return fetch(`${url}?${params}`, {
            credentials: 'same-origin',
        }).then((response) => response.json()).then((response) => {
            let data = response && response.header;

            /* istanbul ignore else */
            if (data) {
                /* istanbul ignore else */
                if (data.traveler || data.owner) {
                    data.login = false;
                    data.signUp = false;
                    data.displaySignUpLink = false;
                } else {
                    data.traveler = false;
                    data.owner = false;
                }

                /* istanbul ignore if */
                if (!data.flags) {
                    data.flags = null;
                }

                /* istanbul ignore else */
                if (!data.globalizationSelector) {
                    data.globalizationSelector = null;
                }

                /* istanbul ignore else */
                if (!data.localeSwitcher) {
                    data.localeSwitcher = null;
                }

                this.setState(data);
            }
        }).catch(/* istanbul ignore next */(error) => {
            console.log(error); // eslint-disable-line no-console
        });
    }

    render() {
        /**
         * @type {any} siteHeaderProps
         */
        let {children, ...siteHeaderProps} = this.props;
        let dataUrl = this.formatDataUrl(siteHeaderProps.dataUrl);
        delete siteHeaderProps.data;
        delete siteHeaderProps.dataUrl;
        delete siteHeaderProps.fetchUserData;
        delete siteHeaderProps.mcsData;
        return (
            <ConnectorContext.Provider value={{dataUrl}}>
                <SiteHeader {...siteHeaderProps} {...this.state}>
                    {children}
                </SiteHeader>
            </ConnectorContext.Provider>
        );
    }
}
